// Sugalarm — colored badge used for zone labels and connection state.

import type { CSSProperties, ReactNode } from "react";
import styles from "./Pill.module.css";
import { statusPillClass } from "./statusPill";

export type PillTone = "urgent" | "warn" | "ok" | "neutral" | "accent";
// Cycle 57: optional larger variant for kiosk surfaces that need a more
// readable badge at kitchen-counter distance. Default ("md") matches
// the cycle-pre-57 pill exactly so every existing caller is unaffected.
export type PillSize = "md" | "lg";
// Cycle 451.2: "status" is the glucose status pill — white ink on an
// opaque state-tinted backing (StatusPill.module.css) so it reads over any
// photo. "badge" (the default) is every other use, unchanged.
export type PillAppearance = "badge" | "status";

interface Props {
  tone?: PillTone;
  size?: PillSize;
  appearance?: PillAppearance;
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
  /** Cycle 451.4: e.g. "status", so a confirmation is announced. */
  role?: string;
}

export function Pill({
  tone = "neutral",
  size = "md",
  appearance = "badge",
  children,
  style,
  className,
  role,
}: Props) {
  const toneClass =
    appearance === "status" ? statusPillClass(tone) : styles[tone];
  const sizeClass = size === "lg" ? styles.lg : undefined;
  return (
    <span
      className={[styles.pill, toneClass, sizeClass, className]
        .filter(Boolean)
        .join(" ")}
      style={style}
      role={role}
    >
      {children}
    </span>
  );
}
